import React, { useRef, useEffect } from "react";
import * as d3 from "d3";

import { useDimensions } from "./useDimensions";
import useIsMobile from "./useIsMobile";

const MiddleAgedMen = ({ data }) => {
  const svgRef = useRef();
  const containerRef = useRef(null);
  const dimensions = useDimensions(containerRef);
  const isMobile = useIsMobile();

  const timelineEvents = [
    { year: 1992, text: "<span class='tooltip__num'><b>1992</span>年</b></br>邓小平“南巡”", offset: 10, mobileOffset: 10 },
    { year: 2001, text: "<span class='tooltip__num'><b>2001</span>年</b></br>中国加入世贸组织", offset: 10, mobileOffset: 40 },
    { year: 2013, text: "<span class='tooltip__num'><b>2013</span>年</b></br>习近平就任国家主席", offset: 10, mobileOffset: 45 },
    { year: 2018, text: "<span class='tooltip__num'><b>2018</span>年</b></br>习近平修宪", offset: -3, mobileOffset: -3 },
    { year: 2020, text: "<span class='tooltip__num'><b>2020</span>年</b></br>疫情开始", offset: -200, mobileOffset: -180 },
    { year: 2022, text: "<span class='tooltip__num'><b>2022</span>年年末</b></br>“清零政策” 结束", offset: 45, mobileOffset: 50 },
  ];

  useEffect(() => {

    const margin = { top: 35, right: 0, bottom: 40, left: 25 };
    // const width = 600 + margin.left + margin.right;
    // const height = 250 + margin.top + margin.bottom;
    const { width, height } = dimensions;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height + margin.bottom + margin.top);

    d3.select(svgRef.current).selectAll('*').remove();


    const xScale = d3.scaleLinear()
      .domain([1980, 2025])
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.gdpgrowth)])
      .nice()
      .range([height - margin.bottom - 100, margin.top]);

    const line = d3.line()
      .x(d => xScale(d.year))
      .y(d => yScale(d.gdpgrowth));

    svg.append("text")
      .attr("class","axes")
      .attr("x", 0)
      .attr("y", 12)
      .text("中国人均GDP增长率");

    
    svg.append("g")
      .attr("class", "svg-grid")
      .attr("transform", `translate(${margin.left}, 0)`)
      .call(
        d3.axisLeft(yScale)
        .tickSize(-width + margin.left + margin.right)
        .tickFormat("")
      )
      .select(".domain")
      .attr("stroke", "none");

    svg.append("g")
      .attr("class", "svg-grid")
      .attr("transform", `translate(0, ${height - margin.bottom - 100})`)
      .call(
        d3.axisBottom(xScale)
        .ticks(35)
        .tickSize(-height + 175)
        .tickFormat("")
      )
      .select(".domain")
      .attr("stroke", "none");

    

    svg.append("g")
      .attr("transform", `translate(0, ${height - margin.bottom - 100})`)
      .attr("class", "x-axis")
      .call(d3.axisBottom(xScale)
        .tickFormat(d3.format("d"))
        .ticks(5)
        .tickSize(0)
        .tickPadding(7)
        );

    svg.append("g")
      .attr("transform", `translate(${margin.left}, 0)`)
      .attr("class", "y-axis")
      .call(d3.axisLeft(yScale)
        .ticks(5)
        .tickSize(0)
        .tickPadding(10)
        .tickFormat((d) => d !== 0 ? d+`%` : "")
        )
      .select(".domain")
      .attr("opacity", 0);

    svg.append("g")
      .selectAll(".line")
      .data([data])
      .enter()
      .append("path")
      .attr("class", "line")
      .attr("d", line)
      .attr("fill", "none")
      .attr("stroke", "var(--china)")
      .attr("stroke-width", 2);

      const timelineWithGDP = timelineEvents.map(event => ({
        ...event,
        gdpgrowth: data.find(d => Number(d.year) === Number(event.year))?.gdpgrowth || null
      }));


    

    svg.append("g")
      .selectAll(".timeline-text")
      .data(timelineEvents)
      .enter()
      .append("g")
      .each(function(d) {
        const group = d3.select(this);
        
        // Vertical line for each timeline event
        group.append("line")
          .attr("x1", xScale(d.year))
          .attr("y1", height - margin.bottom - 105)  // Start at the x-axis
          .attr("x2", xScale(d.year))
          // .attr("y2", height - margin.bottom - 100 + d.offset)
          .attr("y2", height - margin.bottom - 70 + d.offset)
          .attr("stroke", "var(--yellow)")
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.7);

        // Event text
        group.append("foreignObject")
          .attr("class", "callout")
          .attr("x", isMobile ? xScale(d.year) - 25 : xScale(d.year) - 40) 
          .attr("y", isMobile ? (220 + d.mobileOffset) : (240 + d.offset))
          .attr("width", isMobile ? 50 : 90) 
          .attr("height", isMobile ? 90 : 60)  // Define height of the foreignObject
          .append("xhtml:div")
          .html(d.text)  // Insert HTML content
          .style("opacity", 1)
          .style("color", "var(--yellow)")
          .style("background", "#151515");
      });

      svg.append("g")
      .selectAll(".timeline-point")
      .data(timelineWithGDP.filter(d => d.gdpgrowth !== null))
      .enter()
      .append("circle")
      .attr("class", "timeline-point")
      .attr("cx", d => xScale(d.year))
      .attr("cy", d => yScale(d.gdpgrowth))
      .attr("r", 3.5)
      .attr("fill", "var(--china)")
      .attr("stroke", "white")
      .attr("stroke-opacity", 0.7);

  }, [data, dimensions]);


  return (
    <div className="center-container">
        <div className="article-body">
            <p className="subheader">在中国，40岁的中年男性人生轨迹是怎样的呢？</p>
            
            <p>他们成长于八九十年代，赶上“改革开放”的最高峰，这也是中国转型期剧烈的时期。国内生产总值<a href="https://qks.sufe.edu.cn/J/PDFFull/A160824005518.pdf" target="_blank">翻倍</a>，而与此一路同时增长的，是中国<a href="http://sociology.cssn.cn/xscg/zxwz/202101/W020210125398150669416.pdf" target="_blank">收入差距</a>。“如果在这个时候被抛下，或者经历很大的波折，跟周围人的相对剥夺感要更重一些。”媒体人李厚辰跟歪脑解释，特别在为人父后，这种“相对剥夺感”，在中年男性身上呈现出来，是一种全方位的压力。</p>
            </div>
            
            <div ref={containerRef} style={{ height:"350px", width:"100%"}}>
              <svg ref={svgRef}></svg>
            </div>


          <div className="article-body">
          <p>疫情后，中国开始出现青年<a href="https://cn.nytimes.com/business/20221130/china-protests-covid-jobs/" target="_blank">失业潮</a>。但相比二三十岁的年轻人，“(失业)可能会让你的生活更困难，但对父母和家里其他人来说, 这并非是个重大的生存危机。”社会学家弗里德曼跟歪脑推理，年轻人通常不会是家庭收入的主要来源。而40岁左右的中年男性通常是家庭的经济支柱，上有老下有小，社会也给予他们更重的期待，若无法达到，“不仅会面临物质危机，还会伴随着一种强烈的羞耻感。”弗里德曼说。同时，与年轻人相比，中年男性更难再通过学习知识、技能改变生存现状。</p>
          <p>无差别袭击事件的嫌疑人群体也被称为“<a href="http://www.cicjc.com.cn/info/1040/12815.htm" target="_blank">失意群体</a>”，比起物质层面上的“弱势群体”，“失意群体”还更多强调心理层面的失落与被剥夺感。日本<a href="https://www.nippon.com/cn/in-depth/d00790/" target="_blank">学者</a>指出，“失意群体”背后更深层的原因之一在于他们长期的诉求无法得到满足。在这样的状态下，失意的中年男性更有可能在绝望中产生暴烈的行为。</p>

    
        </div>

        
    </div>
  )
}

export default MiddleAgedMen